<template>
  <div class="survey-page page-max-width">
    <QHeader v-if="$q.screen.lt.sm" class="bg-white text-primary">
      <QToolbar>
        <QBtn flat round dense icon="icon-f-arrow-left" @click="goBack" />
        <QToolbarTitle>
          {{ $t("ETAB.PAGES.SURVEY.@PAGE_TITLE") }}
        </QToolbarTitle>
        <QBtn flat round dense icon="icon-f-info" @click="openInfo" />
      </QToolbar>
    </QHeader>

    <QPageContainer>
      <QPage padding>
        <QPullToRefresh no-mouse @refresh="refreshAll">
          <ExtraTipbox>
            <span>{{ $t("ETAB.PAGES.SURVEY.@TIMESHEET_MISSING") }}</span>
            <br />
            <a @click="contactEtabCRA">
              <QIcon name="icon-f-mail" />
              <span>{{ $t("ETAB.PAGES.SURVEY.@CLICK_HERE") }}</span>
            </a>
          </ExtraTipbox>

          <section v-if="extraSchedules && extraSchedules.length > 0" class="survey-list">
            <SchedulesExtraList :extra-schedules="extraSchedules" />
          </section>

          <section v-if="extraReviews && extraReviews.length > 0" class="survey-list">
            <ReviewExtraList :extra-reviews="extraReviews" />
          </section>

          <NoSurvey v-if="noSurvey" @on-back="goBack" />

          <ReviewExtraModal
            v-model:opened="reviewExtraModalOpened"
            :extra-review="extraReviewSelected"
            @on-submit-review="onSubmitReview"
            @on-ignore-review="onIgnoreReview"
          />
          <NoShowModal
            v-model:open="noShowExtraModalOpened"
            :reasons="formattedNoShowReasons"
            :message="extraNoShowMessage"
            @set-reason="declareHourlyRecordExtraMissing"
          />
        </QPullToRefresh>
      </QPage>

      <QPageSticky v-if="$q.screen.gt.xs" position="bottom-right" :offset="[10, 8]">
        <QBtn fab icon="icon-f-rotate-cw" color="secondary" @click="refreshAll" />
      </QPageSticky>
    </QPageContainer>
  </div>
</template>

<script>
  import { ContactManager } from "@core/managers/contact.manager";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { EventManager } from "@core/managers/event.manager";
  import { ScrollManager } from "@core/managers/scroll.manager";
  import { SurveyService, EtabProfileService } from "@core/services";
  import { JavascriptHelpers } from "@core/helpers";
  import { StatusBar } from "@core/plugins/mobile";
  import { SURVEY_FAQ_URL } from "@core/constants";
  import { ETAB_EVENTS } from "@etab/config";
  import {
    ACTION_SURVEY_GET_EXTRA_REVIEWS,
    ACTION_SURVEY_GET_EXTRA_SCHEDULES,
    GETTER_SURVEY_GET_EXTRA_REVIEWS,
    GETTER_SURVEY_GET_EXTRA_SCHEDULES,
    ACTION_SURVEY_GET_AMOUNT,
  } from "@etab/stores/types";
  import { useSurveyStore } from "@etab/stores/survey";
  import { mapStores } from "pinia";
  import { NoShowReasonModel } from "@core/models";

  import NoSurvey from "@etab/components/survey/NoSurvey.component.vue";
  import SchedulesExtraList from "@etab/components/survey/SchedulesExtraList.component.vue";
  import ReviewExtraList from "@etab/components/survey/ReviewExtraList.component.vue";
  import ReviewExtraModal from "@etab/components/survey/ReviewExtra.modal.vue";
  import NoShowModal from "@etab/components/survey/NoShow.modal.vue";

  export default {
    components: {
      NoSurvey,
      SchedulesExtraList,
      ReviewExtraModal,
      ReviewExtraList,
      NoShowModal,
    },
    async beforeRouteUpdate(to, from, next) {
      try {
        this.$_startGlobalLoading();
        await this.refreshReviews();
        next();
      } catch (error) {
        NotificationsManager.error();
        throw error;
      } finally {
        this.$_stopGlobalLoading();
      }
    },
    data() {
      return {
        reviewExtraModalOpened: false,
        noShowExtraModalOpened: false,
        extraReviewSelected: undefined,
        extraNoShowMessage: undefined,
        noShowReasons: [],
      };
    },
    computed: {
      ...mapStores(useSurveyStore),
      extraSchedules() {
        return this.surveyStore[GETTER_SURVEY_GET_EXTRA_SCHEDULES] || [];
      },
      extraReviews() {
        return this.surveyStore[GETTER_SURVEY_GET_EXTRA_REVIEWS] || [];
      },
      noSurvey() {
        return this.extraSchedules.length === 0 && this.extraReviews.length === 0;
      },
      formattedNoShowReasons() {
        return this.noShowReasons.map((noShowReason) => {
          return {
            value: noShowReason.getId(),
            label: noShowReason.getLabel(),
          };
        });
      },
    },
    created() {
      ScrollManager.scrollToTop();
      StatusBar.setStyleLight();

      this.events = [
        { name: ETAB_EVENTS.HOURLY_RECORD.DECLARE_MISSING, action: this.onOpenNoShowExtra },
        { name: ETAB_EVENTS.REVIEW.OPEN_REVIEW, action: this.onOpenReviewExtra },
        { name: ETAB_EVENTS.REVIEW.DECLARE_MISSING, action: this.onDeclareExtraMissing },
        { name: ETAB_EVENTS.REVIEW.IGNORE_REVIEW, action: this.onIgnoreReview },
        { name: ETAB_EVENTS.REVIEW.UPDATE_SCHEDULE, action: this.onUpdateSchedule },
        { name: ETAB_EVENTS.REVIEW.CONFIRM_SCHEDULE, action: this.onConfirmSchedule },
      ];
      this.registerEvents();
    },
    beforeUnmount() {
      this.unregisterEvents();
    },
    mounted() {
      this.$_stopGlobalLoading();
    },
    methods: {
      async getNoShowReasons() {
        if (this.noShowReasons?.length === 0) {
          try {
            this.$_startGlobalLoading();
            const data = await SurveyService.getNoShowReasons();
            this.noShowReasons = data.map((noShowReason) => new NoShowReasonModel(noShowReason));
          } catch (error) {
            NotificationsManager.error();
            throw error;
          } finally {
            this.$_stopGlobalLoading();
          }
        }
      },
      registerEvents() {
        EventManager.registerAllEvents(this.events);
      },
      unregisterEvents() {
        EventManager.unregisterAllEvents(this.events);
      },
      refreshReviews() {
        return this.surveyStore[ACTION_SURVEY_GET_EXTRA_REVIEWS]();
      },
      refreshSchedules() {
        return this.surveyStore[ACTION_SURVEY_GET_EXTRA_SCHEDULES]();
      },
      getSurveysAmount() {
        return this.surveyStore[ACTION_SURVEY_GET_AMOUNT]();
      },
      async refreshAll(refresherDone) {
        const isFromPullToRefresh = typeof refresherDone === "function";
        try {
          if (!isFromPullToRefresh) {
            this.$_startGlobalLoading();
          }
          await Promise.all([this.refreshReviews(), this.refreshSchedules(), this.getSurveysAmount()]);
        } catch (error) {
          NotificationsManager.error({ message: "ERRORS.@SOMETHING_WENT_WRONG" });
          throw error;
        } finally {
          this.$_stopGlobalLoading(); // in case there's a loading
          if (isFromPullToRefresh) {
            refresherDone();
          }
        }
      },
      openInfo() {
        JavascriptHelpers.openUrl(SURVEY_FAQ_URL, "_blank");
      },
      goBack() {
        this.$router.go(-1);
      },
      openReviewExtraModal() {
        this.reviewExtraModalOpened = true;
      },
      closeReviewExtraModal() {
        this.reviewExtraModalOpened = false;
      },
      onOpenReviewExtra({ review }) {
        this.extraReviewSelected = review;
        this.openReviewExtraModal();
      },
      openNoShowExtraModal() {
        this.noShowExtraModalOpened = true;
      },
      closeNoShowExtraModal() {
        this.noShowExtraModalOpened = false;
      },
      onDeclareExtraMissing({ review }) {
        const name = review.getExtraFirstname();
        const date = review.getDatesMoment().start.format("dddd DD/MM");
        const message = this.$filters.whitespacedPunctuation(this.$t("ETAB.PAGES.EXTRA.EXTRA_MISSING_DIALOG.@MESSAGE", { date }));
        this.$q
          .dialog({
            title: this.$t("ETAB.PAGES.EXTRA.EXTRA_MISSING_DIALOG.@TITLE", { name }),
            message,
            ok: {
              label: this.$t("ETAB.PAGES.EXTRA.EXTRA_MISSING_DIALOG.@CONFIRM"),
              color: "secondary",
            },
            cancel: this.$t("ETAB.PAGES.EXTRA.EXTRA_MISSING_DIALOG.@CLOSE"),
          })
          .onOk(() => this.declareExtraMissing({ review }));
      },
      async declareExtraMissing({ review }) {
        try {
          this.$_startGlobalLoading();
          await SurveyService.declareExtraMissingFromReview({ review });
          NotificationsManager.success({ message: "SUCCESS.@EXTRA_DECLARED_MISSING_SUCCESS" });
          await this.refreshAll();
        } catch (error) {
          this.$_stopGlobalLoading();
          NotificationsManager.error();
          throw error;
        }
      },
      onOpenNoShowExtra({ schedule }) {
        this.getNoShowReasons();
        const date = schedule.getDatesDisplay();
        const message = this.$filters.whitespacedPunctuation(this.$t("ETAB.PAGES.EXTRA.EXTRA_MISSING_DIALOG.@MESSAGE", { date }));
        this.extraReviewSelected = schedule;
        this.extraNoShowMessage = message;
        this.openNoShowExtraModal();
      },
      async contactEtabCRA() {
        const etabId = await EtabProfileService.getEtabId();
        const subject = this.$t("EMAILS.@CRA_EMAIL_SUBJECT", { id: etabId });
        ContactManager.contactEtabCRA({ subject });
      },
      async onSubmitReview({ review }) {
        try {
          this.$_startGlobalLoading();
          await SurveyService.postExtraReview({ review });
          NotificationsManager.success({ message: "SUCCESS.@REVIEW_SAVED_SUCCESS" });
          await this.refreshAll();
        } catch (error) {
          this.$_stopGlobalLoading();
          NotificationsManager.error();
          throw error;
        }
      },
      async onIgnoreReview({ review }) {
        try {
          this.$_startGlobalLoading();
          await SurveyService.ignoreReview({ review });
          await this.refreshAll();
        } catch (error) {
          this.$_stopGlobalLoading();
          NotificationsManager.error();
          throw error;
        }
      },
      async declareHourlyRecordExtraMissing(reasonId) {
        try {
          this.$_startGlobalLoading();
          await SurveyService.postNoShowReason({ shiftId: this.extraReviewSelected.getShiftId(), reasonId });
          NotificationsManager.success({ message: "SUCCESS.@EXTRA_DECLARED_MISSING_SUCCESS" });
          await this.refreshAll();
        } catch (error) {
          this.$_stopGlobalLoading();
          NotificationsManager.error();
          throw error;
        } finally {
          this.closeNoShowExtraModal();
        }
      },
      async onUpdateSchedule({ schedule }) {
        try {
          this.$_startGlobalLoading();
          await SurveyService.updateSchedule({ schedule });
          NotificationsManager.success({ message: "SUCCESS.@SCHEDULE_SAVED_SUCCESS" });
          if (schedule.isLastShift() && !schedule.isAlreadyReviewed()) {
            const review = schedule.getReviewFromSchedule();
            this.onOpenReviewExtra({ review });
          }
          await this.refreshAll();
        } catch (error) {
          this.$_stopGlobalLoading();
          NotificationsManager.contactEtabSupport();
          throw error;
        }
      },
      async onConfirmSchedule({ schedule }) {
        try {
          this.$_startGlobalLoading();
          await SurveyService.confirmSchedule({ schedule });
          NotificationsManager.success({ message: "SUCCESS.@SCHEDULE_SAVED_SUCCESS" });
          if (schedule.isLastShift() && !schedule.isAlreadyReviewed()) {
            const review = schedule.getReviewFromSchedule();
            this.onOpenReviewExtra({ review });
          }
          await this.refreshAll();
        } catch (error) {
          this.$_stopGlobalLoading();
          NotificationsManager.contactEtabSupport();
          throw error;
        }
      },
    },
  };
</script>

<style lang="scss">
  .survey-page {
    .survey-list {
      margin: 30px 0;
    }
  }
</style>
